import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, apiRequest } from '../services/apiClient';

/**
 * Loads the tenant-wide expiring/expired documents list. Returns:
 *   { items, status, isLoading, error, refetch, filters, setFilters }
 *
 *   status === 'forbidden' — caller lacks employees.documents.read.
 *   status === 'loading' / 'loaded' / 'error' otherwise.
 *
 * Filters are owned by the caller; pass `enabled: false` to skip
 * fetching entirely (e.g. for users without the permission).
 */
export function useExpiringDocuments({
  withinDays = 30,
  includeExpired = true,
  limit = 200,
  enabled = true
} = {}) {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState(enabled ? 'loading' : 'idle');
  const [error, setError] = useState('');
  const abortRef = useRef(null);

  const fetchPage = useCallback(async () => {
    if (!enabled) { setStatus('idle'); return; }
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setStatus('loading');
    setError('');

    const params = new URLSearchParams();
    params.set('withinDays', String(withinDays));
    params.set('includeExpired', includeExpired ? 'true' : 'false');
    params.set('limit', String(limit));

    try {
      const data = await apiRequest(`/api/employees/documents/expiring?${params.toString()}`, {
        signal: controller.signal
      });
      if (controller.signal.aborted) return;
      setItems(Array.isArray(data?.items) ? data.items : []);
      setStatus('loaded');
    } catch (err) {
      if (controller.signal.aborted) return;
      if (err instanceof ApiError && err.status === 403) {
        setItems([]);
        setStatus('forbidden');
        setError(err.message || 'Expiring documents are restricted.');
        return;
      }
      setStatus('error');
      setError(err?.message || 'Unable to load expiring documents.');
    }
  }, [enabled, withinDays, includeExpired, limit]);

  useEffect(() => {
    fetchPage();
    return () => { if (abortRef.current) abortRef.current.abort(); };
  }, [fetchPage]);

  return { items, status, isLoading: status === 'loading', error, refetch: fetchPage };
}
